import { Fancybox } from '@fancyapps/ui';

// ─────────────────────────────────────────────────────────────────────────────
// GF field ID mapping
// Fill these in once the Gravity Forms are built. The key is the data field
// name returned by the AJAX endpoint; the value is the GF field ID (integer).
//
// Find field IDs in GF: Forms → Edit → click the field → Field ID in settings.
// ─────────────────────────────────────────────────────────────────────────────
const PROFILE_FIELD_MAP = {
	organization_name: 5, // GF field ID for org name
	phone_number: 7, // GF field ID for phone
	website: null, // GF field ID for website
	email: 2, // GF field ID for email
};

const PROGRAM_FIELD_MAP = {
	post_title: 1, // GF field ID for program title
	post_content: 4, // GF field ID for program description/content
	// ACF-backed scalar fields (values arrive under data.acf) — IDs must
	// match save_program_scalar_fields() in Mightily\Core
	phone: 14,
	email: 15,
	website: 16,
	price: 17,
};

// Hidden field in the program form that stores the post ID.
// Leave empty when adding (creates new post); JS writes it when editing.
const PROGRAM_POST_ID_FIELD = 5; // GF field ID for the hidden post_id field

// ─────────────────────────────────────────────────────────────────────────────

const cfg = window.userProfileConfig || {};

// Static form modals — no swipe gestures or drag-to-close; dragging fights
// with selecting/inputting text in the form fields.
const MODAL_OPTS = {
	dragToClose: false,
	Carousel: { gestures: false },
};

function removeMember(memberId, listItem) {
	if (!cfg.ajaxUrl || !cfg.nonce) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action:    'remove_organization_member',
			nonce:     cfg.nonce,
			member_id: memberId,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			listItem.remove();
		});
}

function deleteProgram(postId, card) {
	if (!cfg.ajaxUrl || !cfg.nonce) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action:  'remove_program',
			nonce:   cfg.nonce,
			post_id: postId,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			card.remove();
		});
}

function setGfField(formId, fieldId, value) {
	if (!formId || !fieldId) return;
	const el = document.getElementById(`input_${formId}_${fieldId}`);
	if (!el) return;

	if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
		el.value = value;
		el.dispatchEvent(new Event('change', { bubbles: true }));
	} else {
		el.value = value;
		el.dispatchEvent(new Event('input', { bubbles: true }));
	}
}

// Check/select the inputs of an ACF-driven choice field (a GF field tagged
// with the `acf-choices--{name}` CSS class — see functions/gravityforms).
// Values come straight from ACF, so they match the input values verbatim.
function setAcfChoiceField(name, value) {
	const field = document.querySelector(`#modal-program .acf-choices--${CSS.escape(name)}`);
	if (!field) return;

	const values = (Array.isArray(value) ? value : [value])
		.filter((v) => v !== null && v !== undefined && v !== '')
		.map(String);

	field.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
		input.checked = values.includes(input.value);
	});
	field.querySelectorAll('select option').forEach((opt) => {
		opt.selected = values.includes(opt.value);
	});
}

function clearAcfChoiceFields() {
	document.querySelectorAll('#modal-program [class*="acf-choices--"]').forEach((field) => {
		field.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
			input.checked = false;
		});
		field.querySelectorAll('select').forEach((select) => {
			select.selectedIndex = select.multiple ? -1 : 0;
		});
	});
}

function clearProgramForm() {
	const formId = cfg.forms && cfg.forms.program;
	if (!formId) return;

	// Clear the post ID hidden field so a blank value signals "new post"
	if (PROGRAM_POST_ID_FIELD) {
		setGfField(formId, PROGRAM_POST_ID_FIELD, '');
	}

	Object.values(PROGRAM_FIELD_MAP).forEach((fieldId) => {
		if (fieldId) setGfField(formId, fieldId, '');
	});

	clearAcfChoiceFields();
}

function fetchProfileAndPopulate() {
	if (!cfg.ajaxUrl || !cfg.nonce) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action: 'get_user_profile_data',
			nonce: cfg.nonce,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			const data = response.data;
			const formId = cfg.forms && cfg.forms.editProfile;

			Object.entries(PROFILE_FIELD_MAP).forEach(([key, fieldId]) => {
				if (fieldId && data[key] !== undefined) {
					setGfField(formId, fieldId, data[key]);
				}
			});
		});
}

function fetchProgramAndPopulate(postId) {
	if (!cfg.ajaxUrl || !cfg.nonce || !postId) return;

	fetch(cfg.ajaxUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			action: 'get_program_data',
			nonce: cfg.nonce,
			post_id: postId,
		}),
	})
		.then((r) => r.json())
		.then((response) => {
			if (!response.success) return;
			const data = response.data;
			const formId = cfg.forms && cfg.forms.program;

			if (PROGRAM_POST_ID_FIELD) {
				setGfField(formId, PROGRAM_POST_ID_FIELD, postId);
			}

			Object.entries(PROGRAM_FIELD_MAP).forEach(([key, fieldId]) => {
				if (!fieldId) return;
				const value = data[key] !== undefined ? data[key] : (data.acf || {})[key];
				if (value !== undefined) {
					setGfField(formId, fieldId, value === null ? '' : value);
				}
			});

			// Pre-select the ACF-driven choice fields from the post's values
			clearAcfChoiceFields();
			Object.entries(data.acf || {}).forEach(([name, value]) => {
				setAcfChoiceField(name, value);
			});
		});
}

function setProgramModalHeading(text) {
	const heading = document.querySelector('.js-program-modal-heading');
	if (heading) heading.textContent = text;
}

// Set when the add-member form is submitted while its modal stays open —
// the page reloads once that modal closes so the members list updates.
let memberAdded = false;

// Close the active Fancybox and reload to reflect saved changes.
function handleGfConfirmation(event, formId) {
	// Add Member keeps its modal open so the confirmation stays visible
	const addMemberFormId = Number(cfg.forms && cfg.forms.addMember) || 0;
	if (addMemberFormId && Number(formId) === addMemberFormId) {
		memberAdded = true;
		return;
	}

	const profileForms = [cfg.forms && cfg.forms.editProfile, cfg.forms && cfg.forms.program].filter(Boolean).map(Number);

	if (!profileForms.includes(Number(formId))) return;

	setTimeout(() => {
		Fancybox.close();
		window.location.reload();
	}, 1400);
}

export default {
	init() {
		// Edit Profile
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.js-open-edit-profile')) return;
			Fancybox.show([{ src: '#modal-edit-profile', type: 'inline' }], MODAL_OPTS);
			fetchProfileAndPopulate();
		});

		// Edit Program — fetch existing data and populate
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.js-open-edit-program');
			if (!trigger) return;
			const postId = trigger.dataset.postId;
			setProgramModalHeading('Edit Program');
			Fancybox.show([{ src: '#modal-program', type: 'inline' }], MODAL_OPTS);
			fetchProgramAndPopulate(postId);
		});

		// Add Program — clear the form so no stale values carry over
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.js-open-add-program')) return;
			setProgramModalHeading('Add a Program');
			Fancybox.show([{ src: '#modal-program', type: 'inline' }], MODAL_OPTS);
			clearProgramForm();
		});

		// Add Member — modal stays open after submit; reload on close so the
		// members list reflects any additions
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.js-open-add-member')) return;
			Fancybox.show([{ src: '#modal-add-member', type: 'inline' }], {
				...MODAL_OPTS,
				on: {
					destroy: () => {
						if (memberAdded) {
							memberAdded = false;
							window.location.reload();
						}
					},
				},
			});
		});

		// Remove member (leaders only)
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.js-remove-member');
			if (!trigger) return;
			const memberId = trigger.dataset.memberId;
			const listItem = trigger.closest('.user-profile__member-item');
			if (listItem) removeMember(memberId, listItem);
		});

		// Delete program — confirm first; wp_delete_post trashes it (not
		// permanent), so this is recoverable from wp-admin if clicked by mistake
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.js-delete-program');
			if (!trigger) return;
			if (!window.confirm('Delete this program? This can be undone from the admin dashboard.')) return;
			const postId = trigger.dataset.postId;
			const card = trigger.closest('.user-profile__program-card');
			if (card) deleteProgram(postId, card);
		});

		// GF confirmation → close modal + reload
		jQuery(document).on('gform_confirmation_loaded', handleGfConfirmation);
	},
};
